import { useState, useCallback, useRef } from "react";

// ─── Validators ───────────────────────────────────────

const V = {
  required: (v, label = "This field") =>
    (!v || (typeof v === "string" && !v.trim())) ? `${label} is required` : null,

  email: (v) => {
    if (!v) return null;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : "Enter a valid email address";
  },

  url: (v) => {
    if (!v) return null;
    try { new URL(v.startsWith("http") ? v : `https://${v}`); return null; }
    catch { return "Enter a valid URL"; }
  },

  minLength: (min, label = "This field") => (v) =>
    v && v.length < min ? `${label} must be at least ${min} characters` : null,

  maxLength: (max, label = "This field") => (v) =>
    v && v.length > max ? `${label} must be at most ${max} characters` : null,

  match: (otherValue, msg = "Fields do not match") => (v) =>
    v !== otherValue ? msg : null,

  pattern: (regex, msg = "Invalid format") => (v) =>
    v && !regex.test(v) ? msg : null,

  number: (v) =>
    v !== "" && v !== undefined && isNaN(Number(v)) ? "Enter a valid number" : null,

  min: (minVal, label = "Value") => (v) =>
    v !== "" && Number(v) < minVal ? `${label} must be at least ${minVal}` : null,

  max: (maxVal, label = "Value") => (v) =>
    v !== "" && Number(v) > maxVal ? `${label} must be at most ${maxVal}` : null,
};

// ─── Hook ──────────────────────────────────────────

/**
 * useFormValidation({ email: [V.required, V.email], password: [V.required, V.minLength(6)] })
 * Returns: { errors, touched, validate, validateField, setFieldTouched, isValid, firstErrorRef, scrollToFirstError }
 */
export function useFormValidation(rules = {}) {
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const fieldRefs = useRef({});

  const validateField = useCallback((field, value) => {
    const fieldRules = rules[field];
    if (!fieldRules) return null;
    for (const rule of fieldRules) {
      const err = typeof rule === "function" ? rule(value) : null;
      if (err) return err;
    }
    return null;
  }, [rules]);

  const setFieldTouched = useCallback((field, value) => {
    setTouched(t => ({ ...t, [field]: true }));
    const err = validateField(field, value);
    setErrors(e => {
      const next = { ...e };
      if (err) next[field] = err; else delete next[field];
      return next;
    });
    return !err;
  }, [validateField]);

  const validate = useCallback((values) => {
    const newErrors = {};
    const allTouched = {};
    for (const field of Object.keys(rules)) {
      allTouched[field] = true;
      const err = validateField(field, values[field]);
      if (err) newErrors[field] = err;
    }
    setTouched(allTouched);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [rules, validateField]);

  const reset = useCallback(() => {
    setErrors({});
    setTouched({});
  }, []);

  const isValid = Object.keys(errors).length === 0;

  const registerRef = useCallback((field) => (el) => { if (el) fieldRefs.current[field] = el; }, []);

  const scrollToFirstError = useCallback(() => {
    const firstErrField = Object.keys(rules).find(f => errors[f]);
    if (firstErrField && fieldRefs.current[firstErrField]) {
      fieldRefs.current[firstErrField].scrollIntoView({ behavior: "smooth", block: "center" });
      fieldRefs.current[firstErrField].focus?.();
    }
  }, [errors, rules]);

  return { errors, touched, validate, validateField, setFieldTouched, reset, isValid, registerRef, scrollToFirstError };
}

// ─── URL Normalizer ───────────────────────────────

export function normalizeUrl(url) {
  if (!url) return url;
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

// ─── Social handle sanitizer ──────────────────────

export function sanitizeHandle(handle) {
  if (!handle) return handle;
  return handle.trim().replace(/^@/, "").replace(/\s+/g, "");
}

export { V };
export default useFormValidation;
